import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import { generateQueryArguments, timezone } from "@/utils";

import Button from "@/components/Atoms/Button";
import ModalMotion, {
  AnimateDirection,
} from "@/components/Molecules/ModalMotion";
import NameInputV2 from "../RightSection/Form/Molecules/NameInput";
import PhoneInputV2 from "../RightSection/Form/Molecules/PhoneInput";
import CTASuccessView from "@/components/Molecules/CTASuccessView";
import {
  ButtonContainer,
  FormContainer,
  FormWrapper,
  HeaderContainer,
  HeaderSubTitleTitle,
  HeaderTitle,
} from "./style";
import { callbackFormInputSchema } from "./schema";
import {
  postPackageSave as postNutritionCallbackData,
  saveLead,
} from "@/redux/LandingPageReducer";
import { DEFAULT_URL_ARGUMENTS } from "@/utils/Constants";

function CallbackForm({ handleCallbackFormView, defaultSlug }) {
  const dispatch = useAppDispatch();
  const urlsArguments = generateQueryArguments() || DEFAULT_URL_ARGUMENTS;

  const { countryCodeData: countryCodes } = useAppSelector(
    (state) => state.dashboard
  );

  const { isPaymentLoading: isLoading, paymentSuccess: success } =
    useAppSelector((state) => state.landingPage);

  const formMethods = useForm({
    mode: "onBlur",
    resolver: yupResolver(callbackFormInputSchema),
    defaultValues: {
      mobileCountryCode: countryCodes?.default?.code || "+91",
      mobile: "",
      name: "",
    },
  });
  const {
    handleSubmit,
    control,
    getValues,
    formState: { isSubmitSuccessful },
  } = formMethods;

  const onBlur = () => {
    const { name, mobileCountryCode: countryCode, mobile, email } = getValues();

    dispatch(
      saveLead({
        data: {
          is_lead: true,
          name,
          phone_prefix: countryCode,
          phone: mobile,
          email,
          package_slug: defaultSlug,
          urls_arguments: urlsArguments,
          payment_mode: "",
          timezone,
        },
      })
    );
  };

  const onSubmit = () => {
    const { name, mobileCountryCode: countryCode, mobile, email } = getValues();

    let apiData = {
      is_callback_request: true,
      name,
      phone_prefix: countryCode,
      mobile: mobile,
      email,
      package_slug: defaultSlug,
      urls_arguments: urlsArguments,
      timezone: timezone,
    };
    dispatch(postNutritionCallbackData({ data: apiData })).then(() => {
      setTimeout(() => {
        handleCallbackFormView();
      }, 60000);
    });
  };

  const HeaderView = (
    <HeaderContainer>
      <HeaderTitle variant="heading4">Sign up</HeaderTitle>
      <HeaderSubTitleTitle variant="paragraph1">
        Book your spot - we'll get in touch to set you up
      </HeaderSubTitleTitle>
    </HeaderContainer>
  );

  const FormView = (
    <FormContainer>
      <NameInputV2
        name={"name"}
        control={control}
        namePlaceholder="Your name"
        inputFontSize="14px"
        inputLineHeight="24px"
      />

      <PhoneInputV2
        control={control}
        name="mobile"
        countryCodes={countryCodes}
        mobilePlaceholder={"Your mobile"}
        inputFontSize="14px"
        inputLineHeight="24px"
        showPhoneIcon
        handleBlur={onBlur}
      />
    </FormContainer>
  );

  const ButtonView = (
    <ButtonContainer>
      <Button
        type="submit"
        style={{
          width: "226px",
          height: "56px",
        }}
        disabled={isLoading}
        isLoading={isLoading}
      >
        Request a callback
      </Button>
    </ButtonContainer>
  );
  return (
    <ModalMotion
      overlayClosable
      onClose={handleCallbackFormView}
      width="512px"
      animateDirection={AnimateDirection.CENTER}
      borderRadius="16px"
      showClose
      iconTopPosition="20px"
      iconRightPosition="20px"
      bodyPadding="48px 32px 32px 32px"
    >
      {(!isSubmitSuccessful || isLoading) && (
        <FormProvider {...formMethods}>
          <FormWrapper>
            <>
              {HeaderView}
              <form onSubmit={handleSubmit(onSubmit)}>
                {FormView}
                {ButtonView}
              </form>
            </>
          </FormWrapper>
        </FormProvider>
      )}
      {success && isSubmitSuccessful && <CTASuccessView />}
    </ModalMotion>
  );
}

export default CallbackForm;
